import { useState } from 'react';
import { api, fmt, type QueueItem } from '../api.ts';
import type { MoveResult } from './MovePartDialog.tsx';
import SplitTable, { moveSides } from './SplitTable.tsx';

type Rest = 'SPLIT' | 'SHORT';

export type MoveOnResult =
  | ({ kind: 'SPLIT' } & MoveResult)
  | { kind: 'FINISHED'; jobComplete: boolean; nextStage: { name: string; isDispatch: boolean } | null;
      quantity: number; shortOfOrder: number };

export default function MoveOnDialog({ item, onClose, onDone }: {
  item: QueueItem;
  onClose: () => void;
  onDone: (r: MoveOnResult) => void;
}) {
  const run = Number(item.production_quantity);
  const ordered = Number(item.ordered_quantity);
  const recorded = Number(item.produced_quantity);
  const next = item.next_stage_name;
  const stage = item.stage_name;
  const [qty, setQty] = useState(String(run));
  const [rest, setRest] = useState<Rest>(next ? 'SPLIT' : 'SHORT');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const q = Number(qty);
  const whole = qty.trim() !== '' && Number.isInteger(q) && q > 0;
  const problem = !whole ? ''
    : q > run ? `At most ${fmt(run)}`
    : q < recorded ? `${fmt(recorded)} already recorded. Enter at least that.`
    : '';
  const valid = whole && !problem;
  const short = valid && q < ordered;
  const left = ordered - q;
  const choice: Rest = next ? rest : 'SHORT';
  const goes = next ? `${fmt(q)} go to ${next}` : null;
  const splits = valid && short && choice === 'SPLIT';

  const changes = !valid ? []
    : !short ? [`${stage} finishes`, ...(goes ? [goes] : [])]
    : choice === 'SPLIT' ? [`The new part goes to ${next}`, `${item.job_number} stays at ${stage}`]
    : [`${stage} finishes ${fmt(left)} short of the order`, ...(goes ? [goes] : [])];

  const title = next ? `Move ${item.job_number} to ${next}` : `Finish ${stage}`;

  async function go() {
    if (!valid) return;
    setBusy(true); setError('');
    try {
      const r = await api.post<MoveOnResult>(`/api/production/job-stages/${item.job_stage_id}/move-on`,
        { quantity: q, ...(short ? { rest: choice } : {}) });
      onDone(r);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not move it on');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="backdrop" onClick={() => !busy && onClose()}>
      <div className="modal" style={{ width: 440 }} role="dialog" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>{title}</h3>
          <p>{item.customer_name} · {item.box_name} · at {stage}</p>
        </div>
        <div className="modal-body">
          {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="move-on-qty">Quantity</label>
            <input id="move-on-qty" type="number" min="1" step="1" autoFocus value={qty}
                   onFocus={(e) => e.target.select()}
                   onChange={(e) => setQty(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter') void go(); }} />
            <div className={problem ? 'hint pp-warn' : 'hint'}>
              {problem || [`Run ${fmt(run)}`, `ordered ${fmt(ordered)}`,
                           recorded > 0 ? `${fmt(recorded)} recorded` : null].filter(Boolean).join(' · ')}
            </div>
          </div>

          {short && next && (
            <fieldset className="field" style={{ border: 0, padding: 0, margin: '16px 0 0' }}>
              <legend style={{ marginBottom: 8 }}>The rest</legend>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, cursor: 'pointer' }}>
                <input type="radio" name="rest" checked={rest === 'SPLIT'} onChange={() => setRest('SPLIT')} />
                <span>Still to make · stays at {stage}</span>
              </label>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
                <input type="radio" name="rest" checked={rest === 'SHORT'} onChange={() => setRest('SHORT')} />
                <span>Not making · {stage} finishes short</span>
              </label>
            </fieldset>
          )}

          {splits && <SplitTable sides={moveSides(item.job_number, ordered, run, q)} ups={item.ups} />}

          {changes.length > 0 && (
            <ul className="undo-list" style={{ marginTop: 16 }}>
              {changes.map((c) => <li key={c}>{c}</li>)}
            </ul>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={() => void go()} disabled={busy || !valid}>
            {busy ? 'Moving…'
              : next ? (valid ? `Move ${fmt(q)} to ${next}` : `Move to ${next}`)
              : `Finish ${stage}`}
          </button>
        </div>
      </div>
    </div>
  );
}
